// IQBible API: Fetch resources for a specific verse (commentary, cross-references, word studies, translations)
// Usage: fetchVerseResources({ bookName, chapter, verse, translation })

const API_BASE = 'https://iq-bible.p.rapidapi.com';
const RAPIDAPI_HOST = 'iq-bible.p.rapidapi.com';
const RAPIDAPI_KEY = '3270e78284mshaa01852a7d7edfcp175c02jsn3bb189bea437';

function getBookId(bookName) {
  const BOOK_NAME_TO_ID = {
    'Genesis': '01', 'Exodus': '02', 'Leviticus': '03', 'Numbers': '04', 'Deuteronomy': '05',
    'Joshua': '06', 'Judges': '07', 'Ruth': '08', '1 Samuel': '09', '2 Samuel': '10',
    '1 Kings': '11', '2 Kings': '12', '1 Chronicles': '13', '2 Chronicles': '14', 'Ezra': '15',
    'Nehemiah': '16', 'Esther': '17', 'Job': '18', 'Psalms': '19', 'Proverbs': '20',
    'Ecclesiastes': '21', 'Song of Solomon': '22', 'Isaiah': '23', 'Jeremiah': '24', 'Lamentations': '25',
    'Ezekiel': '26', 'Daniel': '27', 'Hosea': '28', 'Joel': '29', 'Amos': '30',
    'Obadiah': '31', 'Jonah': '32', 'Micah': '33', 'Nahum': '34', 'Habakkuk': '35',
    'Zephaniah': '36', 'Haggai': '37', 'Zechariah': '38', 'Malachi': '39',
    'Matthew': '40', 'Mark': '41', 'Luke': '42', 'John': '43', 'Acts': '44',
    'Romans': '45', '1 Corinthians': '46', '2 Corinthians': '47', 'Galatians': '48', 'Ephesians': '49',
    'Philippians': '50', 'Colossians': '51', '1 Thessalonians': '52', '2 Thessalonians': '53', '1 Timothy': '54',
    '2 Timothy': '55', 'Titus': '56', 'Philemon': '57', 'Hebrews': '58', 'James': '59',
    '1 Peter': '60', '2 Peter': '61', '1 John': '62', '2 John': '63', '3 John': '64',
    'Jude': '65', 'Revelation': '66'
  };
  return BOOK_NAME_TO_ID[bookName] || '01';
}

function getHeaders() {
  return {
    'x-rapidapi-host': RAPIDAPI_HOST,
    'x-rapidapi-key': RAPIDAPI_KEY
  };
}

function getVerseId(bookName, chapter, verse) {
  // BBCCCVVV
  const bookId = getBookId(bookName);
  const chapterId = String(chapter).padStart(3, '0');
  const verseId = String(verse).padStart(3, '0');
  return `${bookId}${chapterId}${verseId}`;
}

export async function fetchVerseResources({ bookName, chapter, verse, translation = 'kjv' }) {
  const cacheKey = `iqbibleVerseResources_${bookName}_${chapter}_${verse}_${translation}`;
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const { data, expiry } = JSON.parse(cached);
      if (!expiry || Date.now() < expiry) {
        return data;
      }
    } catch {}
  }
  const verseId = getVerseId(bookName, chapter, verse);
  const bookId = getBookId(bookName);
  // 1. Commentary (only 'gills' supported)
  const commentaryUrl = `${API_BASE}/GetCommentary?verseId=${verseId}&commentaryName=gills`;
  // 2. Cross References
  const crossRefUrl = `${API_BASE}/GetCrossReferences?verseId=${verseId}`;
  // 3. Word Studies (original text)
  const wordStudiesUrl = `${API_BASE}/GetOriginalText?verseId=${verseId}`;
  // 4. Translations (parallel)
  const translationsUrl = `${API_BASE}/GetParallelVerses?verseId=${verseId}`;
  // 5. Book Info
  const bookInfoUrl = `${API_BASE}/GetBookInfo?bookId=${bookId}`;
  // 9. Topics (for this verse)
  const topicsUrl = `${API_BASE}/GetTopics?verseId=${verseId}`;

  // Fetch all in parallel
  const [
    commentaryRes,
    crossRefRes,
    wordStudiesRes,
    translationsRes,
    bookInfoRes,
    topicsRes
  ] = await Promise.all([
    fetch(commentaryUrl, { headers: getHeaders() }),
    fetch(crossRefUrl, { headers: getHeaders() }),
    fetch(wordStudiesUrl, { headers: getHeaders() }),
    fetch(translationsUrl, { headers: getHeaders() }),
    fetch(bookInfoUrl, { headers: getHeaders() }),
    fetch(topicsUrl, { headers: getHeaders() })
  ]);

  async function safeJson(res, fallback) {
    if (!res.ok) return fallback;
    const text = await res.text();
    if (!text || text.trim() === '' || text.trim() === 'null') return fallback;
    try {
      return JSON.parse(text);
    } catch {
      return fallback;
    }
  }

  const [
    commentary,
    crossReferences,
    wordStudies,
    translations,
    bookInfo,
    topics
  ] = await Promise.all([
    commentaryRes.ok ? commentaryRes.text() : '',
    safeJson(crossRefRes, []),
    safeJson(wordStudiesRes, []),
    safeJson(translationsRes, []),
    safeJson(bookInfoRes, null),
    safeJson(topicsRes, [])
  ]);

  const result = {
    commentary,
    crossReferences,
    wordStudies,
    translations,
    bookInfo,
    topics
  };
  localStorage.setItem(cacheKey, JSON.stringify({ data: result, expiry: Date.now() + 24 * 60 * 60 * 1000 }));
  return result;
}

// Fetch only Hebrew/Greek analysis for a verse (original text, transliteration, word-by-word)
export async function fetchHebrewGreekAnalysis({ bookName, chapter, verse }) {
  const verseId = getVerseId(bookName, chapter, verse);
  const url = `${API_BASE}/GetOriginalText?verseId=${verseId}`;
  const res = await fetch(url, { headers: getHeaders() });
  if (!res.ok) return [];
  const text = await res.text();
  if (!text || text.trim() === '' || text.trim() === 'null') return [];
  try {
    return JSON.parse(text);
  } catch {
    return [];
  }
}
